import axios from 'axios';
import apiClient from '../common/apiClient.ts';
import type { PagedRequest } from '../../types/common/common.ts';
import type { VendorPackageSlip, VendorPackageLot } from '../../types/vendor/vendor.types.ts';

export interface PackageSourceInput { lotNumber: string; poNumber: string }
export interface PackageSourceInfo { lotNumber: string; status: string; expiryDate?: string; coaCreatedDate?: string }
export interface PackageDraft { vendorId: string; productId: string; packagerLot: string; poNumbers: string[]; sourceLots: PackageSourceInput[] }
export interface PackageDocument {
  slip: VendorPackageSlip;
  poNumbers: { oid: string; ponumber: string; createdDate: string }[];
  sourceLots: VendorPackageLot[];
  sourceInfo: PackageSourceInfo[];
  poCount: number;
  sourceLotCount: number;
}

const root = '/api/vendor/packaging';
export const packagingApi = {
  vendors: () => apiClient.get<{ vendorId: string; vendorCompany: string | null }[]>(`${root}/vendors`).then(response => response.data),
  products: (vendorId: string) => apiClient.get<{ oid: string; name: string }[]>(`${root}/products`, { params: { vendorId } }).then(response => response.data),
  search: (params: PagedRequest) => apiClient.post<{ items: VendorPackageSlip[]; totalCount: number }>(`${root}/search`, {
    ...params.filters, startIndex: (params.page - 1) * params.pageSize, maximumRows: params.pageSize,
    sortBy: params.sortField, sortDirection: params.sortDirection,
  }).then(response => response.data),
  detail: (id: string, poPage = 1, lotPage = 1) => apiClient.get<PackageDocument>(`${root}/${id}`, { params: { poStart: (poPage - 1) * 20, lotStart: (lotPage - 1) * 20, maximumRows: 20 } }).then(response => response.data),
  duplicate: (vendorId: string, productId: string, lotNumber: string) =>
    apiClient.get<{ exists: boolean }>(`${root}/duplicate`, { params: { vendorId, productId, lotNumber } }).then(response => response.data.exists),
  sourceInfo: (vendorId: string, productId: string, lotNumbers: string[]) =>
    apiClient.post<PackageSourceInfo[]>(`${root}/source-info`, { vendorId, productId, lotNumbers }).then(response => response.data),
  create: (request: PackageDraft) => apiClient.post<{ id: string }>(root, request).then(response => response.data),
  append: (id: string, request: { poNumber?: string; sourceLot?: PackageSourceInput }) => apiClient.post(`${root}/${id}/append`, request),
};

export function packagingError(error: unknown): string {
  if (typeof error === 'string') return error;
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 403) return 'Your account does not have access to this packaging action.';
    if (error.response?.status === 404) return 'The packaging record or assigned selection is unavailable.';
    if (error.response?.status && error.response.status >= 500) return 'Packaging could not be saved or loaded. Please retry.';
    const body = error.response?.data as { error?: string; message?: string; errors?: Record<string, string[]> } | undefined;
    if (body?.errors) return Object.values(body.errors).flat().join(' ');
    if (body?.error || body?.message) return body.error ?? body.message!;
  }
  return error instanceof Error ? error.message : 'Packaging request failed. Please retry.';
}